// Shared catalogue search (start screen + size picker). Ports the classic
// `searchAllSizes` scoring (snapshot L3335-3390) so product codes, "21/sheet",
// page size, shape words and dimensions all find things, ranked sensibly.
import type { LabelSize, SheetLayout } from './types'
import { sheetCapacity } from './constants'

export type Catalogued = LabelSize | SheetLayout

export const SHAPE_OPTIONS: { id: string; label: string }[] = [
  { id: 'all', label: 'All shapes' },
  { id: 'rect', label: '▬ Rectangle' },
  { id: 'round', label: '● Round' },
  { id: 'oval', label: '⬭ Oval' },
  { id: 'square', label: '■ Square' },
]

export function isSheet(x: Catalogued): x is SheetLayout {
  return (x as SheetLayout).columns !== undefined
}

/** Effective shape for filtering ('square' is a sheet-only token; sizes are rect/round/oval). */
export function shapeOf(x: Catalogued): string {
  return x.shape ?? 'rect'
}

function pageSizeWords(s: SheetLayout): string {
  return Math.round(s.pageWidthMM) === 210 ? 'A4' : 'US Letter Letter'
}

/** Everything a user might type to find this entry, lower-cased, space-joined. */
export function buildHaystack(x: Catalogued): string {
  const parts: string[] = [x.id, x.brand ?? '', x.name, x.category ?? '', ...(x.codes ?? []), x.notes ?? '']
  if (isSheet(x)) {
    const cap = sheetCapacity(x)
    parts.push(`${cap}/sheet`, `${cap}/vel`, `${x.columns}x${x.rows}`, `${x.columns}×${x.rows}`, 'sheet',
      `${x.labelWidthMM}x${x.labelHeightMM}`, `${x.labelWidthMM}×${x.labelHeightMM}`, String(x.labelWidthMM), String(x.labelHeightMM),
      pageSizeWords(x))
    const sh = shapeOf(x)
    if (sh === 'round') parts.push('round circle circular')
    else if (sh === 'oval') parts.push('oval ellipse')
    else if (sh === 'square') parts.push('square')
    if ((x.roundedCodes?.length ?? 0) > 0 || (x.codes ?? []).some((c) => /R$/i.test(c))) parts.push('rounded corners')
  } else {
    parts.push(`${x.widthMM}x${x.heightMM}`, `${x.widthMM}×${x.heightMM}`, String(x.widthMM), String(x.heightMM))
    const sh = shapeOf(x)
    if (sh === 'round') parts.push('round circle circular')
    else if (sh === 'oval') parts.push('oval ellipse')
  }
  return parts.join(' ').toLowerCase()
}

/** Relevance score (0 = no match). Weights follow the classic editor. */
export function scoreMatch(x: Catalogued, rawQuery: string): number {
  const q = rawQuery.trim().toLowerCase()
  if (!q) return 1
  let score = 0
  const codes = (x.codes ?? []).map((c) => c.toLowerCase())
  if (codes.includes(q)) score += 100
  else if (codes.some((c) => c.startsWith(q))) score += 60
  else if (codes.some((c) => c.includes(q))) score += 40

  const dimsW = isSheet(x) ? x.labelWidthMM : x.widthMM
  const dimsH = isSheet(x) ? x.labelHeightMM : x.heightMM
  const dimCombo = q.replace(/\s+/g, '').replace('×', 'x')
  if (/^\d+(\.\d+)?x\d+(\.\d+)?$/.test(dimCombo)) {
    const [a, b] = dimCombo.split('x').map(Number)
    if ((a === dimsW && b === dimsH) || (a === dimsH && b === dimsW)) score += 80
  } else if (/^\d+(\.\d+)?$/.test(q) && (Number(q) === dimsW || Number(q) === dimsH)) score += 50

  const brandName = `${x.brand ?? ''} ${x.name}`.toLowerCase()
  if (brandName === q || x.name.toLowerCase() === q) score += 90
  else if (brandName.startsWith(q) || x.name.toLowerCase().startsWith(q)) score += 45
  else if (brandName.includes(q)) score += 20

  if (isSheet(x)) {
    const cap = sheetCapacity(x)
    if (q === String(cap) || q === `${cap}/sheet` || q === `${cap}/vel`) score += 55
    if (pageSizeWords(x).toLowerCase().split(' ').includes(q)) score += 30
  }
  if (x.id.toLowerCase() === q) score += 10
  if (score === 0 && buildHaystack(x).includes(q)) score += 5
  return score
}

/** Rank sizes and sheets for a query (empty query = everything, original order). */
export function searchCatalogue<T extends Catalogued>(query: string, items: T[]): T[] {
  const q = query.trim()
  if (!q) return items
  return items
    .map((item, i) => ({ item, i, s: scoreMatch(item, q) }))
    .filter((r) => r.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((r) => r.item)
}
